import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Save,
  Copy,
  Trash2,
  ArrowLeft,
  Users,
  PanelLeftClose,
  PanelLeftOpen,
  Loader2,
} from 'lucide-react';
import { api } from '../lib/api';
import { toast } from '../components/Toast';
import { useScenarioStore } from '../store/scenarioStore';
import type {
  AutomationSolution,
  Client,
  ClientProfile,
  Model,
  Scenario,
  ScenarioWorkspace,
  SpendSet,
  StaffPlan,
} from '@rc/engine/types';
import IntakeForm from '../components/IntakeForm';
import BasicScenarioIntake from '../components/BasicScenarioIntake';
import ResultsView from '../components/ResultsView';
import {
  findPublishedPlan,
  type FindPublishedPlan,
  type PublishPlanArgs,
} from '../components/results/publishPlan';
import { formatMarketShort } from '../components/intake/MarketPicker';
import ScenarioPicker from '../components/ScenarioPicker';
import NewScenarioDialog from '../components/NewScenarioDialog';
import ClientLanding from '../components/ClientLanding';
import type { AuthUser, ModelSummary } from '../lib/api';
import { canDeleteClientContent } from '../lib/nbaScope';
import { parseGeoclientId, withGeoclientQuery } from '../lib/geoclientContext';
import { scenarioWorkspace } from '@rc/engine';

export default function ClientWorkspace({ user }: { user: AuthUser }) {
  const { clientId, scenarioId } = useParams<{ clientId?: string; scenarioId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [staffPlans, setStaffPlans] = useState<StaffPlan[]>([]);
  const [spendSets, setSpendSets] = useState<SpendSet[]>([]);
  const [automationSolutions, setAutomationSolutions] = useState<AutomationSolution[]>([]);
  const [models, setModels] = useState<ModelSummary[]>([]);
  const [linkedStaffPlan, setLinkedStaffPlan] = useState<StaffPlan | null>(null);
  const [showNewScenario, setShowNewScenario] = useState<ScenarioWorkspace | null>(null);
  const [loadingClient, setLoadingClient] = useState(false);
  const [loadingScenario, setLoadingScenario] = useState(false);
  const [savingScenario, setSavingScenario] = useState(false);
  const [switchingModel, setSwitchingModel] = useState(false);
  const [inputsCollapsed, setInputsCollapsed] = useState(false);
  const { scenario, model, result, dirty, setScenario, markClean } = useScenarioStore();
  const canDeleteContent =
    clientId != null ? canDeleteClientContent(user, clientId) : user.permissions.canDeleteContent;
  const geoclientId =
    parseGeoclientId(searchParams.get('geoclientId')) ||
    parseGeoclientId(searchParams.get('groupId'));

  // Initial load: models always
  useEffect(() => {
    api.listModelSummaries().then(setModels).catch(() => setModels([]));
  }, []);

  // Record geoclient workspace access once per tab session (debounce refresh duplicates).
  useEffect(() => {
    const rawGeoclientId =
      searchParams.get('geoclientId') || searchParams.get('groupId');
    if (!rawGeoclientId || !clientId) return;
    const storageKey = `gia-geoclient-access:${user.userId}:${rawGeoclientId}`;
    if (sessionStorage.getItem(storageKey)) return;
    sessionStorage.setItem(storageKey, '1');
    api.recordGeoclientAccess(rawGeoclientId).catch(() => {
      sessionStorage.removeItem(storageKey);
    });
  }, [clientId, searchParams, user.userId]);

  // Whenever clientId changes, load the client metadata + scenarios + plans.
  // Do NOT auto-load a scenario into the store here — the landing screen will
  // handle that; we only load the editor when scenarioId is also present.
  useEffect(() => {
    if (!clientId) {
      setClient(null);
      setScenarios([]);
      setStaffPlans([]);
      useScenarioStore.getState().reset();
      return;
    }
    setLoadingClient(true);
    Promise.all([
      api.getClient(clientId),
      api.listScenarios(clientId),
      api.listStaffPlans(clientId).catch(() => [] as StaffPlan[]),
      api.listSpendSets(clientId).catch(() => [] as SpendSet[]),
      api.listAutomationSolutions(clientId).catch(() => [] as AutomationSolution[]),
    ])
      .then(([c, list, plans, sets, autos]) => {
        setClient(c);
        setScenarios(list);
        setStaffPlans(plans);
        setSpendSets(sets);
        setAutomationSolutions(autos);
      })
      .catch((err) => toast('error', err.message))
      .finally(() => setLoadingClient(false));
  }, [clientId]);

  // When a specific scenarioId is in the URL, load that scenario into the
  // store so the editor view can render it.
  useEffect(() => {
    if (!clientId || !scenarioId) {
      // Clear the editor scenario only when navigating *out* of an editor URL.
      if (!scenarioId) useScenarioStore.getState().reset();
      return;
    }
    let cancelled = false;
    setLoadingScenario(true);
    (async () => {
      try {
        const sc = await api.getScenario(clientId, scenarioId);
        const cached = models.find((mm) => mm.id === sc.modelId);
        const m =
          cached && 'channels' in cached
            ? (cached as Model)
            : await api.getModel(sc.modelId);
        if (cancelled) return;
        setScenario(sc, m);
      } catch (err) {
        if (!cancelled) toast('error', (err as Error).message);
      } finally {
        if (!cancelled) setLoadingScenario(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, scenarioId, models, setScenario]);

  // Full staff-plan payload (list rows are metadata-only without cells).
  const linkedPlanId = scenario?.profile?.currentStaffPlanId;
  useEffect(() => {
    if (!clientId || !linkedPlanId) {
      setLinkedStaffPlan(null);
      return;
    }
    let cancelled = false;
    api
      .getStaffPlan(clientId, linkedPlanId)
      .then((p) => {
        if (!cancelled) setLinkedStaffPlan(p);
      })
      .catch(() => {
        if (!cancelled) setLinkedStaffPlan(null);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, linkedPlanId]);

  // ----- index screen: geoclient list lives at /geoclients --------------------------
  if (!clientId) {
    return <Navigate to="/geoclients" replace />;
  }

  // ----- per-client landing actions (used by ClientLanding) -----------------
  // Opens the styled NewScenarioDialog; actual creation lives in the dialog
  // itself. After the dialog reports a saved scenario we refresh the list,
  // navigate into the editor, and toast.
  const handleNewScenarioFromTemplate = (workspace: ScenarioWorkspace) => {
    if (!client) return;
    setShowNewScenario(workspace);
  };

  const handleScenarioCreated = async (saved: Scenario) => {
    if (!client) return;
    setShowNewScenario(null);
    try {
      const list = await api.listScenarios(client.id);
      setScenarios(list);
    } catch {
      // Non-fatal; list will refresh on next navigation.
    }
    navigate(withGeoclientQuery(`/clients/${client.id}/scenarios/${saved.id}`, geoclientId));
    toast('success', `Created "${saved.name}"`);
  };

  const handleCloneScenarioFromCard = async (src: Scenario) => {
    if (!client) return;
    const newName = prompt('Name for the new scenario version', `${src.name} v2`);
    if (!newName) return;
    const newId = crypto.randomUUID();
    const now = new Date().toISOString();
    try {
      const fullSource = await api.getScenario(client.id, src.id);
      const cloned: Scenario = {
        ...fullSource,
        id: newId,
        name: newName,
        createdAt: now,
        updatedAt: now,
      };
      const saved = await api.createScenario(client.id, cloned, geoclientId);
      const list = await api.listScenarios(client.id);
      setScenarios(list);
      navigate(withGeoclientQuery(`/clients/${client.id}/scenarios/${saved.id}`, geoclientId));
      toast('success', `Cloned to "${saved.name}"`);
    } catch (err) {
      toast('error', (err as Error).message);
    }
  };

  const handleDeleteScenarioFromCard = async (target: Scenario) => {
    if (!client) return;
    if (!confirm(`Delete scenario "${target.name}"?`)) return;
    try {
      await api.deleteScenario(client.id, target.id, geoclientId);
      const list = await api.listScenarios(client.id);
      setScenarios(list);
      toast('info', 'Scenario deleted.');
    } catch (err) {
      toast('error', (err as Error).message);
    }
  };

  /** Publish one of a scenario's output matrices as a saved staff plan. The
   *  matrix supplies its own kind and provenance; this fills in which scenario
   *  and model it came from. Any kind other than 'actual' is what keeps the
   *  plan out of the Actual slot used for cost savings.
   *
   *  With `replaceId` the matrix overwrites a plan it published earlier, so
   *  re-saving after a few more edits refreshes that plan rather than leaving a
   *  trail of near-identical ones. The server refuses to change an existing
   *  plan's kind or originating scenario, so this can only update its own. */
  const handlePublishPlan = async (args: PublishPlanArgs) => {
    if (!client || !scenario) return;
    const now = new Date().toISOString();
    const plan: StaffPlan = {
      id: args.replaceId || crypto.randomUUID(),
      clientId: client.id,
      name: args.name,
      modelId: scenario.modelId,
      createdAt: now,
      updatedAt: now,
      cells: args.cells,
      kind: args.kind,
      source: {
        adjustedCellCount: 0,
        ...args.source,
        scenarioId: scenario.id,
        scenarioName: scenario.name,
        modelId: scenario.modelId,
        generatedAt: now,
      },
    };
    const saved = args.replaceId
      ? await api.saveStaffPlan(client.id, plan)
      : await api.createStaffPlan(client.id, plan);
    setStaffPlans((prev) => [saved, ...prev.filter((p) => p.id !== saved.id)]);
    toast(
      'info',
      args.replaceId
        ? `Updated "${saved.name}" in this client's staff plans.`
        : `Saved "${saved.name}" to this client's staff plans.`,
    );
  };

  /** Which saved plan a matrix's "Save current" action would overwrite. Lives
   *  here because this screen owns the client's plan list; the matrices only ask
   *  about the kind and horizon they are showing. */
  const findPublished = useCallback<FindPublishedPlan>(
    (kind, horizon) =>
      scenario ? findPublishedPlan(staffPlans, scenario.id, kind, horizon) : undefined,
    [staffPlans, scenario],
  );

  const handleDeleteStaffPlan = async (target: StaffPlan) => {
    if (!client) return;
    if (!confirm(`Delete staff plan "${target.name}"?`)) return;
    try {
      await api.deleteStaffPlan(client.id, target.id, geoclientId);
      const list = await api.listStaffPlans(client.id);
      setStaffPlans(list);
      toast('info', 'Staff plan deleted.');
    } catch (err) {
      toast('error', (err as Error).message);
    }
  };

  const handleDeleteSpendSet = async (target: SpendSet) => {
    if (!client) return;
    if (!confirm(`Delete spend set "${target.name}"?`)) return;
    try {
      await api.deleteSpendSet(client.id, target.id, geoclientId);
      const list = await api.listSpendSets(client.id);
      setSpendSets(list);
      toast('info', 'Spend set deleted.');
    } catch (err) {
      toast('error', (err as Error).message);
    }
  };

  const handleDeleteAutomationSolution = async (target: AutomationSolution) => {
    if (!client) return;
    if (!confirm(`Delete automation solution set "${target.name}"? Scenarios that applied it keep their copy.`)) return;
    try {
      await api.deleteAutomationSolution(client.id, target.id, geoclientId);
      const list = await api.listAutomationSolutions(client.id);
      setAutomationSolutions(list);
      toast('info', 'Automation solution set deleted.');
    } catch (err) {
      toast('error', (err as Error).message);
    }
  };

  // ----- editor screen actions ----------------------------------------------
  const handleSave = async () => {
    if (!scenario || !client || !dirty || savingScenario) return;
    const scenarioToSave = scenario;
    setSavingScenario(true);
    try {
      await api.saveScenario(client.id, scenarioToSave, geoclientId);
      markClean();
      toast('success', `Saved "${scenarioToSave.name}"`);
      const list = await api.listScenarios(client.id);
      setScenarios(list);
    } catch (err) {
      toast('error', (err as Error).message);
    } finally {
      setSavingScenario(false);
    }
  };

  const handleCloneScenarioFromEditor = async () => {
    if (!scenario) return;
    await handleCloneScenarioFromCard(scenario);
  };

  // Repoints the scenario at a different model. The picker only knows model
  // summaries, so fetch the full model (spend/level/cost tables) before
  // swapping it into the store and recalculating.
  const handleModelChange = async (modelId: string) => {
    if (!scenario || !model || modelId === model.id || switchingModel) return;
    setSwitchingModel(true);
    try {
      const full = await api.getModel(modelId);
      useScenarioStore.getState().setModel(full);
      toast('info', `Switched to "${full.name}"`);
    } catch (err) {
      toast('error', (err as Error).message);
    } finally {
      setSwitchingModel(false);
    }
  };

  const handleDeleteScenarioFromEditor = async () => {
    if (!scenario || !client) return;
    if (!confirm(`Delete scenario "${scenario.name}"?`)) return;
    try {
      await api.deleteScenario(client.id, scenario.id, geoclientId);
      const list = await api.listScenarios(client.id);
      setScenarios(list);
      const next = list[0];
      navigate(
        next
          ? withGeoclientQuery(`/clients/${client.id}/scenarios/${next.id}`, geoclientId)
          : withGeoclientQuery(`/clients/${client.id}`, geoclientId),
      );
      toast('info', 'Scenario deleted.');
    } catch (err) {
      toast('error', (err as Error).message);
    }
  };

  // ----- per-client landing screen (no scenarioId) --------------------------
  if (!scenarioId) {
    if (loadingClient || !client) {
      return (
        <div className="px-10 py-8">
          <div className="text-slate-500">Loading client workspace…</div>
        </div>
      );
    }
    // Header chips show industry / market — model is no longer pinned at
    // the client level (each scenario carries its own model now).
    const headerBits: string[] = [];
    if (client.industry) headerBits.push(client.industry);
    if (client.market) headerBits.push(formatMarketShort(client.market));
    return (
      <div className="flex flex-col min-h-full">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="px-8 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <Link to="/clients" className="text-slate-400 hover:text-slate-700 shrink-0" title="Back to clients">
                <ArrowLeft size={18} />
              </Link>
              <div className="min-w-0">
                <h1 className="text-lg font-semibold truncate">{client.name}</h1>
                {headerBits.length > 0 && (
                  <div className="text-xs text-slate-500 truncate">
                    {headerBits.map((bit, i) => (
                      <span key={i}>
                        {i > 0 && ' · '}
                        <span className="text-slate-700">{bit}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
        <div className="flex-1 px-8 py-6 w-full max-w-[1600px] 2xl:max-w-[1800px] mx-auto">
          <ClientLanding
            client={client}
            models={models}
            scenarios={scenarios}
            staffPlans={staffPlans}
            spendSets={spendSets}
            automationSolutions={automationSolutions}
            canDelete={canDeleteContent}
            geoclientId={geoclientId}
            onNewScenario={handleNewScenarioFromTemplate}
            onCloneScenario={handleCloneScenarioFromCard}
            onDeleteScenario={handleDeleteScenarioFromCard}
            onDeleteStaffPlan={handleDeleteStaffPlan}
            onDeleteSpendSet={handleDeleteSpendSet}
            onDeleteAutomationSolution={handleDeleteAutomationSolution}
          />
        </div>
        {showNewScenario && (
          <NewScenarioDialog
            client={client}
            scenarios={scenarios}
            staffPlans={staffPlans}
            models={models}
            geoclientId={geoclientId}
            initialWorkspace={showNewScenario}
            onClose={() => setShowNewScenario(null)}
            onCreated={handleScenarioCreated}
          />
        )}
      </div>
    );
  }

  // ----- scenario editor screen --------------------------------------------
  if (loadingClient || loadingScenario || !client || !scenario || !model) {
    return (
      <div className="px-10 py-8">
        <div className="text-slate-500">Loading scenario…</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to={withGeoclientQuery(`/clients/${client.id}`, geoclientId)}
              className="text-slate-400 hover:text-slate-700 shrink-0"
              title="Back to client"
            >
              <ArrowLeft size={18} />
            </Link>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold truncate">{client.name}</h1>
                <span
                  className={
                    scenarioWorkspace(scenario) === 'basic'
                      ? 'pill text-[10px] bg-sky-50 text-sky-800'
                      : 'pill text-[10px] bg-slate-100 text-slate-600'
                  }
                >
                  {scenarioWorkspace(scenario) === 'basic'
                    ? 'Scenarios - Basic'
                    : 'Scenarios - Advanced'}
                </span>
                {dirty && <span className="pill-purple">unsaved</span>}
              </div>
              <div className="text-xs text-slate-500 truncate">
                Scenario: <span className="text-slate-700">{scenario.name}</span>
                {' · '}
                Model: <span className="text-slate-700">{model.name}</span>
                {client.industry && (
                  <>
                    {' · '}
                    <span className="text-slate-700">{client.industry}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ScenarioPicker
              scenarios={scenarios}
              currentId={scenario.id}
              onPick={(id) =>
                navigate(withGeoclientQuery(`/clients/${client.id}/scenarios/${id}`, geoclientId))
              }
            />
            <Link
              to={withGeoclientQuery(
                `/clients/${client.id}/staff-plans/${
                  scenario.profile.currentStaffPlanId || 'new'
                }?from=${scenario.id}`,
                geoclientId,
              )}
              className="btn-secondary"
              title="Open the current staff plan editor"
            >
              <Users size={14} /> Staff plan
            </Link>
            <button onClick={handleCloneScenarioFromEditor} className="btn-secondary" title="Clone scenario">
              <Copy size={14} /> Clone
            </button>
            {canDeleteContent && (
              <button onClick={handleDeleteScenarioFromEditor} className="btn-secondary" title="Delete">
                <Trash2 size={14} />
              </button>
            )}
            <button
              type="button"
              onClick={handleSave}
              className="btn-primary disabled:cursor-not-allowed disabled:opacity-70"
              disabled={!dirty || savingScenario}
            >
              {savingScenario ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Save size={14} />
              )}
              {savingScenario ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </header>

      {savingScenario && (
        <div
          className="border-b border-brand-purple-100 bg-brand-purple-50 px-8 py-2 text-sm text-brand-purple-800"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" />
            Saving scenario changes...
          </div>
        </div>
      )}

      <div
        className={
          inputsCollapsed
            ? 'flex-1 px-8 py-6 flex gap-4 w-full'
            : 'flex-1 px-8 py-6 grid grid-cols-1 xl:grid-cols-[660px_minmax(0,1fr)] gap-6 w-full'
        }
      >
        {inputsCollapsed ? (
          <button
            type="button"
            onClick={() => setInputsCollapsed(false)}
            title="Show inputs"
            className="self-start sticky top-6 flex flex-col items-center gap-2 px-2 py-3 rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-brand-purple-700 hover:border-brand-purple-200 hover:bg-brand-purple-50/40 transition-colors"
          >
            <PanelLeftOpen size={16} />
            <span className="text-[10px] font-semibold uppercase tracking-wider [writing-mode:vertical-rl] rotate-180">
              Inputs
            </span>
          </button>
        ) : (
          <div className="space-y-4 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
                Inputs
              </h2>
              <button
                type="button"
                onClick={() => setInputsCollapsed(true)}
                title="Collapse inputs"
                className="text-slate-400 hover:text-slate-700 transition-colors p-1 -m-1"
              >
                <PanelLeftClose size={16} />
              </button>
            </div>
            {scenarioWorkspace(scenario) === 'basic' ? (
              <BasicScenarioIntake
                model={model}
                profile={scenario.profile}
                models={models}
                clientId={client.id}
                scenarioId={scenario.id}
                geoclientId={geoclientId}
                onChange={(patch: Partial<ClientProfile>) =>
                  useScenarioStore.getState().setProfile(patch)
                }
                onModelChange={handleModelChange}
                modelSwitching={switchingModel}
              />
            ) : (
              <IntakeForm
                model={model}
                profile={scenario.profile}
                models={models}
                clientId={client.id}
                scenarioId={scenario.id}
                geoclientId={geoclientId}
                onChange={(patch: Partial<ClientProfile>) =>
                  useScenarioStore.getState().setProfile(patch)
                }
                onModelChange={handleModelChange}
                modelSwitching={switchingModel}
              />
            )}
          </div>
        )}
        <div className="flex-1 space-y-4 min-w-0">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">
            Outputs
          </h2>
          {result ? (
            <ResultsView
              model={model}
              result={result}
              profile={scenario.profile}
              staffPlan={
                linkedStaffPlan ||
                staffPlans.find(
                  (p) => p.id === scenario.profile.currentStaffPlanId,
                ) ||
                null
              }
              workspace={scenarioWorkspace(scenario)}
              clientId={client.id}
              geoclientId={geoclientId}
              onProfileChange={(patch: Partial<ClientProfile>) =>
                useScenarioStore.getState().setProfile(patch)
              }
              onPublishPlan={handlePublishPlan}
              findPublishedPlan={findPublished}
              defaultPlanName={scenario.name}
            />
          ) : (
            <div className="card card-body text-slate-500">No result yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
